import DossierSteps from "../DossierSteps";
import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

/* ------------------------------------------------------------------ */
/* Thème local (n'affecte pas le thème global de l'application)        */
/* ------------------------------------------------------------------ */
const COLORS = {
  primary: '#0B6B4B',
  primaryLight: '#E3F4EC',
  danger: '#D93B3B',
  dangerLight: '#FDECEC',
  info: '#2F6FED',
  infoLight: '#E8EFFD',
  warning: '#B7791F',
  warningLight: '#FDF3DC',
  neutral: '#6B7280',
  neutralLight: '#EEF0F3',
  border: '#E5E7EB',
  bg: '#F6F8FA',
  text: '#1F2937',
};

/* ------------------------------------------------------------------ */
/* Données par défaut (à remplacer par vos props / appels API)         */
/* ------------------------------------------------------------------ */
const DEFAULT_DOSSIER = {
  reference: 'MP-2026-024',
  statut: 'Dossier actif',
  vehicule: {
    nom: 'Toyota Corolla Cross 2024',
    immatriculation: 'CE 327 AA 01',
    image: '/assets/images/vehicule1.png', // chemin d'accès de l'image (même chemin que dans votre code existant)
  },
};

const DEFAULT_SUMMARY = {
  attendu: { montant: '425 000 FCFA', detail: 'Prochaine échéance • 15 févr. 2026' },
  encaisse: { montant: '3 825 000 FCFA', detail: '9 échéances sur 36' },
  arrieres: { montant: '425 000 FCFA', detail: '1 échéance en retard' },
};

const DEFAULT_PAYMENTS = [
  { id: 1, date: '15 janv. 2026', montant: '425 000 FCFA', statut: 'paye', mode: 'LewooPay', reference: 'LP250115001' },
  { id: 2, date: '15 févr. 2026', montant: '425 000 FCFA', statut: 'retard', mode: '—', reference: '—' },
  { id: 3, date: '15 déc. 2025', montant: '425 000 FCFA', statut: 'paye', mode: 'LewooPay', reference: 'LP251215003' },
  { id: 4, date: '15 nov. 2025', montant: '425 000 FCFA', statut: 'paye', mode: 'LewooPay', reference: 'LP251115002' },
  { id: 5, date: '15 oct. 2025', montant: '425 000 FCFA', statut: 'pause', mode: '—', reference: '—' },
  { id: 6, date: '15 sept. 2025', montant: '425 000 FCFA', statut: 'paye', mode: 'LewooPay', reference: 'LP250915001' },
];

const DEFAULT_ELIGIBILITY = [
  { key: 'chauffeur', label: 'Compte chauffeur actif (LewooPay)', help: true, enabled: true },
  { key: 'anciennete', label: 'Ancienneté minimale (≥ 6 mois)', enabled: true },
  { key: 'note', label: 'Note minimale (≥ 4,0)', enabled: true },
  { key: 'impaye', label: 'Aucun impayé', enabled: true },
  { key: 'ratio', label: 'Ratio mensualités / revenu (≤ 40 %)', enabled: true },
];

const STATUS_CONFIG = {
  paye: { label: 'Payé', color: COLORS.primary, bg: COLORS.primaryLight },
  retard: { label: 'En retard', color: COLORS.danger, bg: COLORS.dangerLight },
  pause: { label: 'En pause', color: COLORS.neutral, bg: COLORS.neutralLight },
  partiel: { label: 'Partiel', color: COLORS.warning, bg: COLORS.warningLight },
  a_venir: { label: 'À venir', color: COLORS.info, bg: COLORS.infoLight },
};

/* ------------------------------------------------------------------ */
/* Sous-composants                                                     */
/* ------------------------------------------------------------------ */
function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pause;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: 12,
        height: 22,
        borderRadius: '6px',
      }}
    />
  );
}

function SummaryCard({ icon, iconColor, iconBg, label, value, detail, valueColor, borderColor }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 220,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderRadius: 2,
        borderColor: borderColor || COLORS.border,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: iconBg,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: COLORS.neutral }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: valueColor || COLORS.text, lineHeight: 1.2 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: COLORS.neutral }}>
          {detail}
        </Typography>
      </Box>
    </Paper>
  );
}

/* ------------------------------------------------------------------ */
/* Composant principal                                                 */
/* ------------------------------------------------------------------ */
export default function PaymentTracking({
  dossier = DEFAULT_DOSSIER,
  summary = DEFAULT_SUMMARY,
  payments = DEFAULT_PAYMENTS,
  schedule = [], // contenu de l'onglet "Échéancier" (même format que payments)
  eligibility = DEFAULT_ELIGIBILITY,
  suspendLabel = 'Suspendre temporairement',
  saving = false,
  scheduleTotals = null,
  canSuspend = true,
  canSaveSettings = true,
  contracts = [],
  contractsError = false,
  downloadingContractId = null,
  onDownloadContract,
  onRetryContracts,
  onCopy,
  onBack,
  onSuspend,
  onSave,
}) {
  const [tab, setTab] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [settings, setSettings] = useState(eligibility);

  const rows = tab === 0 ? payments : schedule;

  const handleToggle = (key) => {
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, enabled: !s.enabled } : s)));
  };

  const handleSave = () => {
    if (onSave) {
      const values = settings.reduce((acc, s) => ({ ...acc, [s.key]: s.enabled }), {});
      onSave(values);
    }
  };

  return (
    <Box sx={{ bgcolor: COLORS.bg, p: { xs: 2, md: 3 }, color: COLORS.text }}>
      {/* En-tête */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          {onBack ? (
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: 'none', color: COLORS.neutral, px: 0.5, minWidth: 0 }}>
              Gestion des dossiers / {dossier.reference}
            </Button>
          ) : (
            <Typography variant="caption" sx={{ color: COLORS.neutral }}>
              Gestion des dossiers / {dossier.reference}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 700 }}>Suivi des paiements</Typography>
            <Chip
              label={dossier.statut}
              size="small"
              sx={{
                bgcolor: COLORS.primaryLight,
                color: COLORS.primary,
                fontWeight: 600,
                border: `1px solid ${COLORS.primary}`,
              }}
            />
          </Box>
        </Box>

        {/* Bloc véhicule : image + nom + matricule */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 110,
              height: 64,
              borderRadius: 1.5,
              overflow: 'hidden',
              bgcolor: COLORS.neutralLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {dossier.vehicule?.image && !imageError ? (
              <img
                src={dossier.vehicule.image}
                alt={dossier.vehicule.nom || 'Véhicule'}
                onError={() => setImageError(true)}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <DirectionsCarOutlinedIcon sx={{ fontSize: 36, color: COLORS.neutral }} />
            )}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
              {dossier.vehicule?.nom}
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.neutral, letterSpacing: 0.3 }}>
              {dossier.vehicule?.immatriculation}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}><DossierSteps activeStep={5} /></Box>

      {/* Cartes de synthèse */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <SummaryCard
          icon={<CalendarMonthOutlinedIcon />}
          iconColor={COLORS.info}
          iconBg={COLORS.infoLight}
          label="Montant attendu"
          value={summary.attendu.montant}
          detail={summary.attendu.detail}
        />
        <SummaryCard
          icon={<PaymentsOutlinedIcon />}
          iconColor={COLORS.primary}
          iconBg={COLORS.primaryLight}
          label="Montant encaissé"
          value={summary.encaisse.montant}
          detail={summary.encaisse.detail}
        />
        <SummaryCard
          icon={<ErrorOutlineIcon />}
          iconColor={COLORS.danger}
          iconBg={COLORS.dangerLight}
          label="Arriérés"
          value={summary.arrieres.montant}
          detail={summary.arrieres.detail}
          valueColor={COLORS.danger}
          borderColor="#F5C2C2"
        />
      </Box>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, borderColor: COLORS.border }}>
        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, mb: 1.5 }}>
          <DescriptionOutlinedIcon sx={{ color: COLORS.primary }} />Contrats du dossier
        </Typography>
        {contractsError ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontSize: 13, color: COLORS.danger }}>Impossible de charger les contrats.</Typography>
            <Button onClick={onRetryContracts} size="small">Réessayer</Button>
          </Box>
        ) : contracts.length ? contracts.map((contract) => (
          <Box key={contract.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, py: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{contract.label}</Typography>
              <Typography sx={{ color: COLORS.neutral, fontSize: 12 }}>
                {[contract.reference, contract.version != null ? `Version ${contract.version}` : ''].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={downloadingContractId === contract.id ? <CircularProgress size={16} /> : <DownloadOutlinedIcon />}
              disabled={Boolean(downloadingContractId) || !onDownloadContract}
              onClick={() => onDownloadContract(contract)}
              aria-label={`Télécharger le ${contract.label.toLowerCase()} en PDF`}
              sx={{ textTransform: 'none', color: COLORS.primary, borderColor: COLORS.primary }}
            >
              {downloadingContractId === contract.id ? 'Téléchargement…' : 'Télécharger le PDF'}
            </Button>
          </Box>
        )) : <Typography sx={{ fontSize: 13, color: COLORS.neutral }}>Aucun contrat disponible pour ce dossier.</Typography>}
      </Paper>

      {/* Contenu principal */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', lg: 'nowrap' }, alignItems: 'flex-start' }}>
        {/* Colonne gauche : tableau + actions */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: COLORS.border, overflow: 'hidden' }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                px: 2,
                borderBottom: `1px solid ${COLORS.border}`,
                '& .MuiTabs-indicator': { bgcolor: COLORS.primary },
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                '& .Mui-selected': { color: `${COLORS.primary} !important` },
              }}
            >
              <Tab label="Historique" />
              <Tab label="Échéancier" />
            </Tabs>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { color: COLORS.neutral, fontWeight: 600, fontSize: 12 } }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Montant</TableCell>
                    {tab === 1 && <TableCell>Payé</TableCell>}
                    {tab === 1 && <TableCell>Reste dû</TableCell>}
                    <TableCell>Statut</TableCell>
                    {tab === 0 && <TableCell>Mode de paiement</TableCell>}
                    {tab === 0 && <TableCell>Référence LewooPay</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: COLORS.neutral }}>
                        Aucune donnée à afficher
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      // Échéancier : soldée en vert, passée non soldée en rouge.
                      const tint = tab === 1 && row.statut === 'paye' ? COLORS.primaryLight : tab === 1 && row.statut === 'retard' ? COLORS.dangerLight : undefined;
                      return (
                        <TableRow key={row.id} hover sx={{ '& td': { fontSize: 13 }, bgcolor: tint }}>
                          <TableCell>{row.date}</TableCell>
                          <TableCell>{row.montant}</TableCell>
                          {tab === 1 && <TableCell sx={{ color: COLORS.primary, fontWeight: 600 }}>{row.paye}</TableCell>}
                          {tab === 1 && <TableCell sx={{ color: row.statut === 'retard' ? COLORS.danger : COLORS.text, fontWeight: 600 }}>{row.reste}</TableCell>}
                          <TableCell>
                            <StatusChip status={row.statut} />
                          </TableCell>
                          {tab === 0 && <TableCell>{row.mode}</TableCell>}
                          {tab === 0 && (
                            <TableCell>
                              {row.reference && row.reference !== '--' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span>{row.reference}</span>
                                  <Tooltip title="Copier">
                                    <IconButton size="small" aria-label="Copier la référence" onClick={() => onCopy?.(row.reference)}>
                                      <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : '--'}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                  {tab === 1 && scheduleTotals && rows.length > 0 && (
                    <TableRow sx={{ '& td': { fontSize: 13, fontWeight: 700, borderTop: `2px solid ${COLORS.border}` } }}>
                      <TableCell>Total</TableCell>
                      <TableCell>{scheduleTotals.total}</TableCell>
                      <TableCell sx={{ color: COLORS.primary }}>{scheduleTotals.paid}</TableCell>
                      <TableCell sx={{ color: COLORS.danger }}>{scheduleTotals.outstanding}</TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            {canSuspend && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onSuspend}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: COLORS.text,
                borderColor: COLORS.border,
                bgcolor: '#fff',
                flex: 1,
                minWidth: 220,
                py: 1.2,
              }}
            >
              {suspendLabel}
            </Button>
            )}

          </Box>
        </Box>

        {/* Colonne droite : paramètres d'éligibilité */}
        <Paper
          variant="outlined"
          sx={{
            width: { xs: '100%', lg: 340 },
            flexShrink: 0,
            p: 2.5,
            borderRadius: 2,
            borderColor: COLORS.border,
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Paramètres d'éligibilité (global)</Typography>

          {settings.map((s) => (
            <Box
              key={s.key}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
            >
              <Switch
                checked={s.enabled}
                onChange={() => handleToggle(s.key)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    bgcolor: COLORS.primary,
                    opacity: 1,
                  },
                }}
              />
              <Typography sx={{ fontSize: 13, flex: 1 }}>{s.label}</Typography>
              {s.help && <HelpOutlineIcon sx={{ fontSize: 16, color: COLORS.primary }} />}
            </Box>
          ))}

          {canSaveSettings && (
          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              mt: 3,
              py: 1.3,
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: COLORS.primary,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#095A3F', boxShadow: 'none' },
            }}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          )}
        </Paper>
      </Box>
    </Box>
  );
}